/**
 * Comprehensive symbol mapping utility for ALL instrument types
 * Handles ALL variations: MATIC/USD = MATICUSD = MATIC-USD = MATIC_USD = MATICUSDT = MATIC/USDT
 * Supports crypto, forex, stocks, commodities, and all quote currencies
 */

export interface SymbolVariations {
  standard: string; // ETHUSD
  slash: string; // ETH/USD
  hyphen: string; // ETH-USD
  tether: string; // ETHUSDT
  underscore: string; // ETH_USDT
  slashTether: string; // ETH/USDT
  hyphenTether: string; // ETH-USDT
  underscoreTether: string; // ETH_USDT
  base: string; // ETH
  quote: string; // USD
  allVariations: string[]; // All possible variations
}

/**
 * Comprehensive list of quote currencies for proper symbol extraction
 */
const QUOTE_CURRENCIES = [
  // Fiat currencies
  'USD', 'USDT', 'USDC', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'NZD',
  'SEK', 'NOK', 'DKK', 'PLN', 'TRY', 'ZAR', 'MXN', 'BRL', 'SGD', 'HKD',
  'CNY', 'INR', 'KRW', 'RUB',
  // Stablecoins
  'BUSD', 'DAI', 'TUSD', 'PAX', 'USDP',
  // Major crypto quote currencies
  'BTC', 'ETH', 'BNB', 'XRP',
];

/**
 * Normalizes a trading symbol to its standard format (ETHUSD)
 * Removes all common separators: /, -, _
 */
export function normalizeSymbol(symbol: string): string {
  if (!symbol) return '';
  
  // Remove common separators: slash, hyphen, underscore
  return symbol
    .toUpperCase()
    .replace(/[\/\-_]/g, '')
    .trim();
}

/**
 * Extracts base and quote currencies from ANY symbol format
 * Handles: BTC/USD, BTC-USD, BTC_USD, BTCUSD, BTC/USDT, etc.
 */
export function extractCurrencies(symbol: string): { base: string; quote: string } | null {
  if (!symbol) return null;

  // First, try separator-based extraction (most reliable)
  const separators = ['/', '-', '_'];
  for (const separator of separators) {
    if (symbol.includes(separator)) {
      const [base, quote] = symbol.split(separator).map(s => s.trim().toUpperCase());
      if (base && quote) {
        return { base, quote };
      }
    }
  }

  // If no separator, try to extract by matching known quote currencies
  const normalized = normalizeSymbol(symbol);
  
  // Sort quote currencies by length (longest first) to match USDT before USD
  const sortedQuotes = [...QUOTE_CURRENCIES].sort((a, b) => b.length - a.length);
  
  for (const quote of sortedQuotes) {
    if (normalized.endsWith(quote)) {
      const base = normalized.slice(0, -quote.length);
      if (base.length > 0) {
        return { base, quote };
      }
    }
  }
  
  return null;
}

/**
 * Generates ALL possible variations of a symbol
 * Returns comprehensive mapping including hyphenated and all separator types
 */
export function getSymbolVariations(symbol: string): SymbolVariations | null {
  const currencies = extractCurrencies(symbol);
  
  if (!currencies) return null;
  
  const { base, quote } = currencies;
  
  // Generate tether/stablecoin variations
  const tetherQuote = quote === 'USD' ? 'USDT' : quote;
  
  const variations: SymbolVariations = {
    standard: `${base}${quote}`,
    slash: `${base}/${quote}`,
    hyphen: `${base}-${quote}`,
    underscore: `${base}_${quote}`,
    tether: `${base}${tetherQuote}`,
    slashTether: `${base}/${tetherQuote}`,
    hyphenTether: `${base}-${tetherQuote}`,
    underscoreTether: `${base}_${tetherQuote}`,
    base,
    quote,
    allVariations: [],
  };

  // Build comprehensive list of all variations
  variations.allVariations = [
    variations.standard,
    variations.slash,
    variations.hyphen,
    variations.underscore,
  ];

  // Add tether variations only if different from base
  if (tetherQuote !== quote) {
    variations.allVariations.push(
      variations.tether,
      variations.slashTether,
      variations.hyphenTether,
      variations.underscoreTether
    );
  }

  return variations;
}

/**
 * Checks if two symbols match (including ALL variations)
 * Example: MATIC/USD matches MATICUSDT, MATIC-USD, MATIC_USDT, etc.
 */
export function symbolsMatch(symbol1: string, symbol2: string): boolean {
  const normalized1 = normalizeSymbol(symbol1);
  const normalized2 = normalizeSymbol(symbol2);
  
  // Direct match after normalization
  if (normalized1 === normalized2) return true;
  
  // Check if both resolve to same base currency pair
  const variations1 = getSymbolVariations(symbol1);
  const variations2 = getSymbolVariations(symbol2);
  
  if (!variations1 || !variations2) return false;
  
  // Match if base currencies are the same
  if (variations1.base !== variations2.base) return false;
  
  // Match if quotes are compatible (USD = USDT for crypto)
  const compatibleQuotes = (q1: string, q2: string) => {
    if (q1 === q2) return true;
    // USD and USDT are considered equivalent for crypto trading
    if ((q1 === 'USD' && q2 === 'USDT') || (q1 === 'USDT' && q2 === 'USD')) return true;
    return false;
  };
  
  return compatibleQuotes(variations1.quote, variations2.quote);
}

/**
 * Maps a symbol to its display format (slash format by default)
 */
export function toDisplayFormat(symbol: string): string {
  const variations = getSymbolVariations(symbol);
  return variations?.slash || symbol;
}

/**
 * Maps a symbol to API-specific format
 * Supports: binance, coinbase, alpaca, twelvedata, polygon, standard
 */
export function toApiFormat(
  symbol: string,
  apiType: 'binance' | 'coinbase' | 'alpaca' | 'twelvedata' | 'polygon' | 'standard'
): string {
  const variations = getSymbolVariations(symbol);
  
  if (!variations) return symbol;
  
  switch (apiType) {
    case 'binance':
      // Binance uses BTCUSDT format (no separator, prefers USDT)
      return variations.tether;
    case 'coinbase':
      // Coinbase uses BTC-USD format (hyphen separator)
      return variations.hyphen;
    case 'alpaca':
      // Alpaca uses BTC/USD format (slash separator)
      return variations.slash;
    case 'twelvedata':
      // TwelveData uses BTC/USD format (slash separator)
      return variations.slash;
    case 'polygon':
      // Polygon uses BTCUSD format (no separator)
      return variations.standard;
    case 'standard':
    default:
      return variations.standard;
  }
}

/**
 * Validates if a symbol is well-formed
 */
export function isValidSymbol(symbol: string): boolean {
  if (!symbol || typeof symbol !== 'string') return false;
  
  const currencies = extractCurrencies(symbol);
  return currencies !== null && currencies.base.length > 0 && currencies.quote.length > 0;
}

/**
 * Gets comprehensive symbol info including ALL variations
 */
export function getSymbolInfo(symbol: string): {
  original: string;
  normalized: string;
  variations: SymbolVariations | null;
  isValid: boolean;
  allPossibleFormats: string[];
} {
  const variations = getSymbolVariations(symbol);
  
  return {
    original: symbol,
    normalized: normalizeSymbol(symbol),
    variations,
    isValid: isValidSymbol(symbol),
    allPossibleFormats: variations?.allVariations || [symbol],
  };
}

/**
 * Batch convert symbols to a specific API format
 */
export function batchConvertSymbols(
  symbols: string[],
  apiType: 'binance' | 'coinbase' | 'alpaca' | 'twelvedata' | 'polygon' | 'standard'
): string[] {
  return symbols.map(symbol => toApiFormat(symbol, apiType));
}

/**
 * Find matching symbol from a list using fuzzy matching
 * Useful for finding the right symbol format in a list of available symbols
 */
export function findMatchingSymbol(targetSymbol: string, availableSymbols: string[]): string | null {
  const normalized = normalizeSymbol(targetSymbol);
  
  for (const available of availableSymbols) {
    if (symbolsMatch(targetSymbol, available)) {
      return available;
    }
  }
  
  return null;
}

/**
 * Get alternative symbol formats for fallback API calls
 * Returns array of symbol variations to try in order
 */
export function getSymbolFallbacks(symbol: string): string[] {
  const variations = getSymbolVariations(symbol);
  
  if (!variations) return [symbol];
  
  // Return variations in order of common usage
  return [
    variations.slash,        // BTC/USD (most common for APIs)
    variations.tether,       // BTCUSDT (common for crypto)
    variations.standard,     // BTCUSD
    variations.hyphen,       // BTC-USD
    variations.slashTether,  // BTC/USDT
    variations.underscore,   // BTC_USD
  ].filter((v, i, arr) => arr.indexOf(v) === i); // Remove duplicates
}